import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { filter, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

import { BaseComponent } from '../base-component';
import { PlanService } from '@core/services/plan.service';
import { PlanType } from '@core/models/plan.model';

/**
 * Route prefixes that require Professional plan. The overlay blurs the
 * feature behind a centred upgrade card while the underlying route still
 * renders (so the lock is contextual + reversible after upgrade).
 */
const GATED_PREFIXES: Array<{
  prefix: string;
  titleKey: string;
  descKey: string;
  fallbackTitle: string;
  fallbackDesc: string;
}> = [
  {
    prefix: '/finance/time-tracking',
    titleKey: 'planGate.timeTracking.title',
    descKey:  'planGate.timeTracking.desc',
    fallbackTitle: 'Time tracking',
    fallbackDesc:  'Track billable hours, run a live timer, and turn time into invoices automatically.'
  },
  {
    prefix: '/contracts',
    titleKey: 'planGate.contracts.title',
    descKey:  'planGate.contracts.desc',
    fallbackTitle: 'Contracts & e-sign',
    fallbackDesc:  'Send contracts, collect legal e-signatures, and keep a verified audit trail.'
  }
];

@Component({
  selector: 'app-plan-gate',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, TranslateModule],
  templateUrl: './plan-gate.component.html',
  styleUrls: ['./plan-gate.component.scss']
})
export class PlanGateComponent extends BaseComponent {
  private readonly router = inject(Router);
  private readonly planService = inject(PlanService);

  private readonly currentUrl = signal<string>(this.router.url);
  private readonly usage = toSignal(this.planService.usage$, { initialValue: this.planService.getCachedUsage() });

  /** Looks up the gate config for the current route, if any. */
  readonly gate = computed(() => {
    const url = this.currentUrl().split('?')[0];
    return GATED_PREFIXES.find(g => url === g.prefix || url.startsWith(g.prefix + '/')) ?? null;
  });

  /** True when the user is on the Free / Starter plan. */
  readonly isFree = computed(() => {
    const u = this.usage();
    return u ? u.currentPlan === PlanType.Starter : false;
  });

  /** Final visibility: gated route AND user is free. */
  readonly show = computed(() => !!this.gate() && this.isFree());

  constructor() {
    super();
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(e => this.currentUrl.set((e as NavigationEnd).urlAfterRedirects));

    // Make sure we have plan usage cached; safe to call repeatedly.
    if (!this.planService.getCachedUsage()) {
      this.planService.refreshUsage();
    }
  }

  goPricing(): void { this.router.navigate(['/pricing']); }
}
